import { type RefObject, useEffect, useRef } from "react";

/**
 * Focus the referenced element once on mount, with `preventScroll: true` so
 * scroll-snap (chatless variant) keeps ownership of positioning.
 *
 * Intended for read-only widgets (claims, summaries, meducation cards) where
 * keyboard + screen-reader users must land on the widget when it arrives but
 * the widget itself has no first-interactive-element to focus. Pair with
 * `tabIndex={-1}` on the target element so it becomes programmatically
 * focusable without joining the tab order.
 *
 * Guarded so StrictMode double-mount + parent re-render cannot refocus and
 * fight an active user selection.
 */
export function useFocusOnMount<T extends HTMLElement>(
  ref: RefObject<T | null>,
  enabled = true,
): void {
  const didFocusRef = useRef(false);
  useEffect(() => {
    if (!enabled || didFocusRef.current) return;
    const el = ref.current;
    if (!el) return;
    didFocusRef.current = true;
    el.focus({ preventScroll: true });
  }, [ref, enabled]);
}
